import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { SERVICES, CATEGORIES, Service } from '../types';
import { Calendar, Clock, ArrowRight, CheckCircle2 } from 'lucide-react';

interface BookingProps {
  onBack: () => void;
  preSelectedService?: string;
}

export function Booking({ onBack, preSelectedService }: BookingProps) {
  const [step, setStep] = useState(preSelectedService ? 2 : 1);
  const [selectedService, setSelectedService] = useState<Service | null>(
    preSelectedService ? SERVICES.find(s => s.id === preSelectedService) || null : null
  );
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [userData, setUserData] = useState({ name: '', email: '', phone: '' });

  // Dummy time slots
  const timeSlots = ['09:00 AM', '10:30 AM', '12:00 PM', '01:30 PM', '03:00 PM', '04:30 PM', '06:00 PM', '08:00 PM'];

  const handleNext = () => setStep(s => s + 1);
  const handlePrev = () => {
    if (step === 1) onBack();
    else setStep(s => s - 1);
  };

  const submitBooking = (e: React.FormEvent) => {
    e.preventDefault();
    handleNext(); // Go to success step
  };

  return (
    <div className="min-h-screen pt-32 pb-24 px-6 max-w-3xl mx-auto bg-stone-50">
      <div className="mb-12">
        <button 
          onClick={handlePrev}
          className="text-[10px] uppercase tracking-[0.2em] text-mier-light hover:text-mier mb-8 transition-colors flex items-center gap-2"
        >
          &larr; {step === 1 ? 'Back to Home' : 'Previous Step'}
        </button>
        <h1 className="font-serif text-4xl md:text-5xl font-light mb-6 text-mier leading-tight">
          {step === 1 && "Select a Service"}
          {step === 2 && "Choose Date & Time"}
          {step === 3 && "Your Details"}
          {step === 4 && "Booking Confirmed"}
        </h1>
        <p className="text-sm text-stone-600 leading-relaxed font-light max-w-2xl">
          Book your appointment at MIER Beauty Lounge, Aloft Hotel Ground Floor, Al Rawdah, Abu Dhabi, for hair, nails, facials, mesotherapy, lashes, brows, waxing, or bridal beauty.
        </p>
        {step < 4 && (
          <div className="flex gap-3 items-center">
            {[1, 2, 3].map(i => (
              <React.Fragment key={i}>
                <div className={`h-[1px] w-12 ${step >= i ? 'bg-mier' : 'bg-stone-300'}`} />
              </React.Fragment>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence mode="wait">
        {step === 1 && (
          <motion.div
            key="step1"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-16"
          >
            {CATEGORIES.map(cat => {
              const catServices = SERVICES.filter(s => s.category === cat);
              if (catServices.length === 0) return null;
              
              return (
                <div key={cat}>
                  <h2 className="font-serif text-3xl text-mier-light italic mb-8">{cat}</h2>
                  <div className="grid gap-4">
                    {catServices.map(service => (
                      <div 
                        key={service.id}
                        onClick={() => {
                          setSelectedService(service);
                          handleNext();
                        }}
                        className="cursor-pointer group p-6 md:p-8 bg-white border border-stone-200 hover:border-mier transition-all duration-300"
                      >
                        <div className="flex justify-between items-start mb-3">
                          <h3 className="font-medium text-lg text-mier">{service.name}</h3>
                          <span className="font-serif text-xl text-mier">{service.price}</span>
                        </div>
                        <p className="text-stone-500 text-sm mb-6 leading-relaxed bg-white">{service.description}</p>
                        {service.duration && (
                          <div className="flex items-center text-[10px] uppercase tracking-widest text-mier-light group-hover:text-mier transition-colors gap-2">
                            <Clock className="w-3.5 h-3.5" />
                            {service.duration}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </motion.div>
        )}

        {step === 2 && (
          <motion.div
            key="step2"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-10"
          >
            {selectedService && (
              <div className="p-8 bg-white border border-stone-200">
                <p className="text-[10px] uppercase tracking-widest text-mier-light mb-2">Selected Treatment</p>
                <div className="flex justify-between items-center">
                  <h3 className="font-serif text-2xl text-mier">{selectedService.name}</h3>
                  <button onClick={() => setStep(1)} className="text-[10px] uppercase tracking-widest text-mier-light hover:text-mier border-b border-transparent hover:border-mier transition-all">Change</button>
                </div>
              </div>
            )}

            <div>
              <label className="block text-[10px] uppercase tracking-widest text-mier-light mb-4">Select Date</label>
              <input 
                type="date" 
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]} // Quick validation
                className="w-full bg-white border border-stone-200 px-6 py-5 text-mier focus:outline-none focus:border-mier transition-colors font-serif text-lg"
              />
            </div>

            {selectedDate && (
              <motion.div initial={{opacity: 0}} animate={{opacity: 1}}>
                <label className="block text-[10px] uppercase tracking-widest text-mier-light mb-4 mt-8">Available Times</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {timeSlots.map(time => (
                    <button
                      key={time}
                      onClick={() => setSelectedTime(time)}
                      className={`py-4 text-sm transition-all border ${
                        selectedTime === time 
                        ? 'bg-mier text-stone-50 border-mier' 
                        : 'bg-white border-stone-200 text-mier hover:border-mier'
                      }`}
                    >
                      {time}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            <div className="pt-8">
              <button 
                onClick={handleNext}
                disabled={!selectedDate || !selectedTime}
                className="w-full bg-mier text-stone-50 py-5 text-xs uppercase tracking-[0.2em] disabled:opacity-30 disabled:hover:bg-mier hover:bg-mier-light transition-colors flex items-center justify-center gap-3"
              >
                Continue <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}

        {step === 3 && (
          <motion.div
            key="step3"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <form onSubmit={submitBooking} className="space-y-6">
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-mier-light mb-2">Full Name</label>
                <input 
                  required
                  type="text" 
                  value={userData.name}
                  onChange={e => setUserData({...userData, name: e.target.value})}
                  className="w-full bg-white border border-stone-200 px-6 py-4 text-mier focus:outline-none focus:border-mier transition-colors"
                  placeholder="Jane Doe"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-mier-light mb-2">Email Address</label>
                <input 
                  required
                  type="email" 
                  value={userData.email}
                  onChange={e => setUserData({...userData, email: e.target.value})}
                  className="w-full bg-white border border-stone-200 px-6 py-4 text-mier focus:outline-none focus:border-mier transition-colors"
                  placeholder="jane@example.com"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-mier-light mb-2">Phone Number</label>
                <input 
                  required
                  type="tel" 
                  value={userData.phone}
                  onChange={e => setUserData({...userData, phone: e.target.value})}
                  className="w-full bg-white border border-stone-200 px-6 py-4 text-mier focus:outline-none focus:border-mier transition-colors"
                  placeholder="+971 5X XXX XXXX"
                />
              </div>

              <div className="bg-white border border-stone-200 p-8 mt-12 mb-8 space-y-5 text-sm text-mier-light">
                <div className="flex justify-between items-center pb-5 border-b border-stone-100">
                  <span className="font-medium text-mier text-base">{selectedService?.name}</span>
                  <span className="font-serif text-xl text-mier">{selectedService?.price}</span>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3"><Calendar className="w-4 h-4"/> {selectedDate}</div>
                  <div className="flex items-center gap-3"><Clock className="w-4 h-4"/> {selectedTime}</div>
                </div>
              </div>

              <button 
                type="submit"
                className="w-full bg-mier text-stone-50 py-5 text-xs uppercase tracking-[0.2em] hover:bg-mier-light transition-colors"
              >
                Confirm Appointment
              </button>
            </form>
          </motion.div>
        )}

        {step === 4 && (
          <motion.div
            key="step4"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-20 px-6 bg-white border border-stone-200 mt-8"
          >
            <div className="inline-flex items-center justify-center mb-8 text-mier">
              <CheckCircle2 className="w-12 h-12 stroke-[1]" />
            </div>
            <h2 className="font-serif text-4xl leading-tight mb-6 text-mier">We look forward to seeing you, {userData.name.split(' ')[0]}</h2>
            <p className="text-stone-500 mb-12 max-w-md mx-auto leading-relaxed">
              Your appointment for <span className="text-mier font-medium">{selectedService?.name}</span> on <span className="text-mier font-medium">{selectedDate}</span> at <span className="text-mier font-medium">{selectedTime}</span> has been confirmed. We've sent the details to {userData.email}.
            </p>
            <button 
              onClick={onBack}
              className="border border-mier text-mier py-4 px-10 text-xs uppercase tracking-[0.2em] hover:bg-mier hover:text-stone-50 transition-colors"
            >
              Return Home
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
